import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { QueryProvider } from '@anyroad/providers';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import BookingRequestActionCreator from 'action_creators/booking_request';
import PricingStore from 'stores/plugins/pricing';
import BookingRequestStore from 'stores/plugins/booking_request';
import PricingActionCreator from 'action_creators/pricing';
import Dimmer from 'views/shared/ui/dimmer';

import { CheckoutProvider } from './CheckoutContext';
import { CheckoutPageOne } from './CheckoutPageOne';
import { CheckoutPageTwo } from './CheckoutPageTwo';
import type {
  CheckoutContainerProps,
  PersonalInfoFormData,
  GuestInfoFormData,
  PaymentFormData,
  AdditionalInfoFormData,
  PricingData,
  CheckoutQuestion,
} from './types';

const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

const convertPricingStoreToCamelCase = (
  store: any,
  defaults: Record<string, any> = {}
): Record<string, any> => {
  const result: Record<string, any> = {};
  const keys = store.keySeq ? store.keySeq().toArray() : Object.keys(store);

  keys.forEach((key: string) => {
    const camelKey = snakeToCamel(key);
    let value = store.get ? store.get(key) : store[key];

    if (key === 'units' && value && typeof value.toJSON === 'function') {
      value = value.toJSON();
    } else if (key === 'addons' && value && typeof value.toJS === 'function') {
      value = value.toJS();
    }

    if (camelKey in defaults) {
      result[camelKey] = value || defaults[camelKey];
    } else {
      result[camelKey] = value;
    }
  });

  return result;
};

export const CheckoutContainer: React.FC<CheckoutContainerProps> = (props) => {
  const {
    initial_summary_props,
    initial_fully_discounted,
    initial_fields,
    tour_id,
    booking_request_id,
    includes_transport = false,
    tour_settings = {},
    units,
    managed_account_token,
    is_ga4_enabled = false,
    is_booking_invoice = false,
    is_affiliate = false,
    is_unpaid_booking = false,
    is_pay_later_enabled = false,
    is_outside_pay_later_cutoff = false,
    is_rsvp_enabled = false,
    is_guest_info_collection_enabled = false,
    is_guest_of_guest_enabled = false,
    guest_of_guest_max_guests = 0,
    partner_info,
    kiosk_setting,
    stripe_theme,
    will_expire_on,
    show_translate = false,
    is_live = false,
  } = props;

  const [currentPage, setCurrentPage] = useState<1 | 2>(1);
  const [pageOneData, setPageOneData] = useState<Partial<
    PersonalInfoFormData & GuestInfoFormData & AdditionalInfoFormData
  > | null>(
    initial_fields && Object.keys(initial_fields).length > 0
      ? {
          first_name: initial_fields.first_name || '',
          last_name: initial_fields.last_name || '',
          email: initial_fields.email || '',
          phone: initial_fields.phone || '',
        }
      : null
  );

  const [pricingData, setPricingData] = useState<PricingData>({
    bookingFeeFormatted: '',
    discountFormatted: '',
    discountCode: '',
    onlineDiscountFormatted: '',
    depositFormatted: '',
    unitPriceFormatted: '',
    guestsPriceFormatted: '',
    guests: null,
    totalFormatted: '',
    additionalTax: 0,
    additionalTaxFormatted: '',
    additionalTaxLabel: '',
    remainderFormatted: '',
    remainderPaymentDate: '',
    remainderInCash: false,
    remainderInCredit: false,
    remainderChargeDaysOffset: 0,
    remainderInLocalCurrencyFormatted: '',
    totalInLocalCurrency: '',
    totalInLocalCurrencyFormatted: '',
    totalInLocalCurrencyExactFormatted: '',
    paidInCash: false,
    addons: {},
    units: {},
    loading: false,
  });

  const [storeState, setStoreState] = useState({
    total: 1,
    currency: '',
    zeroPricedTour: null as boolean | null,
    fullyDiscounted: initial_fully_discounted,
    paidInCash: null as boolean | null,
    discountApplied: initial_summary_props.request.discount_applied || false,
    discountLoading: false,
    discountErrors: undefined as any,
    loading: false,
    committed: false,
    requiresAuth: false,
    authCompleted: false,
    paidInFull: false,
    privacyPolicyLink: '',
    privacyPolicyLinkText: '',
    hidePolicyLinks: false,
    tourName: '',
  });

  const [i18nLang] = useState<string>('en-US');
  const [selectedAddons] = useState<any[]>([]);
  const previewInitializedRef = useRef(false);

  useEffect(() => {
    const handlePricingChanged = () => {
      const pricingDefaults: Record<string, any> = {
        bookingFeeFormatted: '',
        discountFormatted: '',
        onlineDiscountFormatted: '',
        depositFormatted: '',
        unitPriceFormatted: '',
        guestsPriceFormatted: '',
        guests: null,
        units: {},
        addons: {},
        totalFormatted: '',
        additionalTax: 0,
        additionalTaxFormatted: '',
        additionalTaxLabel: '',
        remainderFormatted: '',
        remainderPaymentDate: '',
        remainderInCash: false,
        remainderInCredit: false,
        remainderChargeDaysOffset: 0,
        remainderInLocalCurrencyFormatted: '',
        paidInCash: false,
        totalInLocalCurrency: '',
        totalInLocalCurrencyFormatted: '',
        totalInLocalCurrencyExactFormatted: '',
        loading: false,
      };

      const convertedPricing = convertPricingStoreToCamelCase(
        PricingStore.state,
        pricingDefaults
      );

      setPricingData({
        ...pricingDefaults,
        ...convertedPricing,
        discountCode: BookingRequestStore.state.get('discount_code') || '',
      } as PricingData);

      const storeDefaults: Record<string, any> = {
        tourName: '',
        privacyPolicyLink: '',
        privacyPolicyLinkText: '',
        hidePolicyLinks: false,
      };

      const convertedStore = convertPricingStoreToCamelCase(
        PricingStore.state,
        storeDefaults
      );

      setStoreState((prev) => ({
        ...prev,
        total: parseFloat(PricingStore.state.get('total')) || 1,
        currency: PricingStore.state.get('currency') || '',
        zeroPricedTour:
          PricingStore.state.get('zero_priced') !== undefined
            ? PricingStore.state.get('zero_priced')
            : null,
        paidInCash:
          PricingStore.state.get('paid_in_cash') !== undefined
            ? PricingStore.state.get('paid_in_cash')
            : null,
        fullyDiscounted: PricingStore.state.get('fully_discounted') || false,
        tourName: convertedStore.tourName,
        privacyPolicyLink: convertedStore.privacyPolicyLink,
        privacyPolicyLinkText: convertedStore.privacyPolicyLinkText,
        hidePolicyLinks: convertedStore.hidePolicyLinks,
      }));
    };

    const handleBookingRequestChanged = () => {
      const storeErrors = BookingRequestStore.state.get('errors');
      let discountErrors: any;

      if (storeErrors) {
        const errorsObj = storeErrors.toJS ? storeErrors.toJS() : storeErrors;
        const errorArray = errorsObj.discount_code || errorsObj.booking_request;

        if (errorArray) {
          discountErrors = {
            toJS: () => (Array.isArray(errorArray) ? errorArray : [errorArray]),
          };
        }
      }

      const discountApplied =
        BookingRequestStore.state.get('discount_applied') || false;
      const discountLoading =
        BookingRequestStore.state.get('discountLoading') || false;
      const loading = BookingRequestStore.state.get('loading') || false;

      setStoreState((prev) => {
        const discountStateChanged =
          prev.discountApplied !== discountApplied ||
          (prev.discountLoading === true && discountLoading === false);

        if (discountStateChanged && !loading) {
          const params: any = initial_summary_props.request.booking_id
            ? { bookingId: initial_summary_props.request.booking_id }
            : {};
          if ((initial_summary_props.request as any).time) {
            params.time = (initial_summary_props.request as any).time;
          }
          PricingActionCreator.preview(params);
        }

        return {
          ...prev,
          loading,
          committed: BookingRequestStore.state.get('committed') || false,
          requiresAuth: BookingRequestStore.state.get('requiresAuth') || false,
          authCompleted:
            BookingRequestStore.state.get('authCompleted') || false,
          paidInFull: BookingRequestStore.state.get('paidInFull') || false,
          discountApplied,
          discountLoading,
          discountErrors,
        };
      });
    };

    PricingStore.changed.add(handlePricingChanged);
    BookingRequestStore.changed.add(handleBookingRequestChanged);

    if (!previewInitializedRef.current) {
      const params: any = initial_summary_props.request.booking_id
        ? { bookingId: initial_summary_props.request.booking_id }
        : {};
      if ((initial_summary_props.request as any).time) {
        params.time = (initial_summary_props.request as any).time;
      }
      PricingActionCreator.preview(params);
      previewInitializedRef.current = true;
    }

    return () => {
      PricingStore.changed.remove(handlePricingChanged);
      BookingRequestStore.changed.remove(handleBookingRequestChanged);
    };
  }, [booking_request_id]);

  useEffect(() => {
    const { authCompleted, paidInFull, committed } = storeState;
    const { is_unpaid_booking: unpaidBooking } = props;

    if (
      authCompleted ||
      paidInFull ||
      (committed && is_live) ||
      (committed && !unpaidBooking)
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      if (i18nLang) {
        urlParams.set('lang', i18nLang);
      }

      let confirmationUrl;
      if (is_live) {
        const queryString = urlParams.toString();
        const queryParams = queryString ? `?${queryString}` : '';
        confirmationUrl = `/live/${booking_request_id}/confirmation${queryParams}`;
      } else if (is_booking_invoice) {
        const queryString = urlParams.toString();
        const queryParams = queryString ? `?${queryString}` : '';
        confirmationUrl = `/payments/${booking_request_id}/confirmation${queryParams}`;
      } else {
        urlParams.set('id', booking_request_id);
        const queryString = urlParams.toString();
        confirmationUrl = `/booking_request/confirmation?${queryString}`;
      }

      window.location.href = confirmationUrl;
    }
  }, [
    storeState.authCompleted,
    storeState.paidInFull,
    storeState.committed,
    booking_request_id,
    i18nLang,
    is_live,
    is_booking_invoice,
    props.is_unpaid_booking,
  ]);

  const handlePageOneSubmit = useCallback(
    (
      data: PersonalInfoFormData & GuestInfoFormData & AdditionalInfoFormData
    ) => {
      const flattenMultipleCheckoutAnswers = (
        questions: CheckoutQuestion[]
      ): any[] => {
        const answers: any[] = [];
        questions.forEach((question) => {
          if (Array.isArray(question.answer)) {
            const questionAnswer = question.answer.filter((ans) => ans);
            questionAnswer.forEach((answer) => {
              answers.push({
                tour_question_id: question.tour_question_id,
                answer,
              });
            });
          } else {
            answers.push({
              tour_question_id: question.tour_question_id,
              answer:
                question.input_type === 'waiver' && !question.answer
                  ? 'no'
                  : question.answer,
            });
          }
        });
        return answers;
      };

      const processedData = { ...data };
      if (data.checkout_questions) {
        processedData.checkout_questions = flattenMultipleCheckoutAnswers(
          data.checkout_questions
        ) as any;
      }

      const isPaymentNeeded =
        storeState.zeroPricedTour !== true &&
        !storeState.fullyDiscounted &&
        storeState.paidInCash !== true &&
        storeState.total > 0;

      if (!isPaymentNeeded) {
        const formatAdditionalGuests = (guests: any[]) => {
          guests.forEach((guest) => {
            const firstName = guest.first_name ? `${guest.first_name} ` : '';
            const lastName = guest.last_name ? guest.last_name : '';
            guest.full_name = firstName + lastName;
          });
          return guests;
        };

        const params = {
          ...processedData,
          id: booking_request_id,
          tour_id,
          unit_configuration: units,
          addon_configuration: selectedAddons,
          i18n: i18nLang,
        };

        if (processedData.checkout_questions) {
          params.checkout_questions = processedData.checkout_questions;
        }

        if (processedData.additional_guests) {
          params.additional_guests = formatAdditionalGuests(
            processedData.additional_guests
          );
        }
        BookingRequestActionCreator.commit(params);
        return;
      }

      setPageOneData(processedData);
      setCurrentPage(2);
      window.history.pushState({ page: 2 }, '', window.location.href);
      window.scrollTo(0, 0);
    },
    [storeState, booking_request_id, tour_id, units, selectedAddons, i18nLang]
  );

  const handlePageTwoSubmit = useCallback(
    (data: PaymentFormData) => {
      const formatAdditionalGuests = (guests: any[]) => {
        guests.forEach((guest) => {
          const firstName = guest.first_name ? `${guest.first_name} ` : '';
          const lastName = guest.last_name ? guest.last_name : '';
          guest.full_name = firstName + lastName;
        });
        return guests;
      };

      const params = {
        ...pageOneData,
        ...data,
        id: booking_request_id,
        tour_id,
        unit_configuration: units,
        addon_configuration: selectedAddons,
        i18n: i18nLang,
      };

      if (pageOneData?.checkout_questions) {
        params.checkout_questions = pageOneData.checkout_questions;
      }

      if (pageOneData?.additional_guests) {
        params.additional_guests = formatAdditionalGuests(
          pageOneData.additional_guests
        );
      }
      BookingRequestActionCreator.commit(params);
    },
    [pageOneData, booking_request_id, tour_id, units, selectedAddons, i18nLang]
  );

  useEffect(() => {
    const handlePopState = () => {
      if (currentPage === 2) {
        setCurrentPage(1);
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentPage]);

  const isLiveBooking = useMemo<boolean>(() => {
    const { request } = initial_summary_props;
    return !!(request.is_live_booking && request.live?.enabled);
  }, [initial_summary_props]);

  const i18nList = useMemo(() => {
    return (initial_summary_props.request as any).i18n_list || [];
  }, [initial_summary_props]);

  const getPricingUnitsItems = useCallback((pricingUnits: any): any[] => {
    if (Array.isArray(pricingUnits)) {
      return pricingUnits;
    }
    if (
      pricingUnits &&
      typeof pricingUnits === 'object' &&
      'items' in pricingUnits &&
      Array.isArray(pricingUnits.items)
    ) {
      return pricingUnits.items;
    }
    if (pricingUnits && typeof pricingUnits === 'object') {
      return Object.values(pricingUnits).filter(
        (item: any) => item && typeof item === 'object' && item.id
      ) as any[];
    }
    return [];
  }, []);

  const isPricingDataReady = useMemo(() => {
    const pricingUnitsItems = getPricingUnitsItems(pricingData?.units);
    return pricingUnitsItems.length > 0;
  }, [pricingData, getPricingUnitsItems]);

  const enrichedUnits = useMemo(() => {
    if (!units || units.length === 0) {
      return units;
    }

    if (!isPricingDataReady) {
      return units;
    }

    const pricingUnitsItems = getPricingUnitsItems(pricingData?.units);
    const pricingUnitsMap = new Map(
      pricingUnitsItems.map((item: any) => [item.id, item])
    );

    return units.map((unit) => {
      const pricingUnit = pricingUnitsMap.get(unit.id);

      return {
        ...unit,
        noun_singular:
          pricingUnit?.noun_singular || unit.noun_singular || 'Guest',
        noun_plural: pricingUnit?.noun_plural || unit.noun_plural || 'Guests',
      };
    });
  }, [units, pricingData, isPricingDataReady, getPricingUnitsItems]);

  const summaryProps = useMemo(() => {
    return {
      ...initial_summary_props,
      checkout_questions: initial_fields?.checkout_questions || [],
    };
  }, [initial_summary_props, initial_fields]);

  const contextValue = {
    flags: props.flags,
    bookingRequestId: booking_request_id,
    tourId: tour_id,
    units: enrichedUnits,
    selectedAddons,
    pricingData,
    summaryProps,
    includesTransport: includes_transport,
    total: storeState.total,
    currency: storeState.currency,
    zeroPricedTour: storeState.zeroPricedTour,
    fullyDiscounted: storeState.fullyDiscounted,
    paidInCash: storeState.paidInCash || false,
    isBookingInvoice: is_booking_invoice,
    isLiveBooking,
    isAffiliate: is_affiliate,
    isUnpaidBooking: is_unpaid_booking,
    isPayLaterEnabled: is_pay_later_enabled,
    isOutsidePayLaterCutoff: is_outside_pay_later_cutoff,
    isRsvpEnabled: is_rsvp_enabled,
    phoneMandatory: initial_summary_props.request.phone_mandatory || false,
    isGuestInfoCollectionEnabled: is_guest_info_collection_enabled,
    isGuestOfGuestEnabled: is_guest_of_guest_enabled,
    guestOfGuestMaxGuests: guest_of_guest_max_guests,
    partnerInfo: partner_info,
    kioskSetting: kiosk_setting,
    managedAccountToken: managed_account_token,
    stripeTheme: stripe_theme,
    tourSettings: tour_settings,
    i18nLang,
    i18nList,
    willExpireOn: will_expire_on,
    showTranslate: show_translate,
    privacyPolicyLink: storeState.privacyPolicyLink,
    privacyPolicyLinkText: storeState.privacyPolicyLinkText,
    hidePolicyLinks: storeState.hidePolicyLinks,
    discountApplied: storeState.discountApplied,
    discountCode: pricingData.discountCode,
    discountLoading: storeState.discountLoading,
    discountErrors: storeState.discountErrors,
    isGA4Enabled: is_ga4_enabled,
    loading: storeState.loading,
  };

  if (!isPricingDataReady && units && units.length > 0) {
    return <Dimmer fixed transparent loader active />;
  }

  return (
    <QueryProvider>
      <CheckoutProvider {...contextValue}>
        {currentPage === 1 ? (
          <CheckoutPageOne
            pageOneData={pageOneData}
            onSubmit={handlePageOneSubmit}
          />
        ) : (
          <CheckoutPageTwo onSubmit={handlePageTwoSubmit} />
        )}
      </CheckoutProvider>
    </QueryProvider>
  );
};

export default withLDConsumer()(CheckoutContainer);
